/**
 * ROI Projection Engine
 *
 * All percentage-based benchmarks are sourced from published industry research.
 * Dollar projections are normalized to a flat 1,000 monthly visitors baseline
 * so clients can scale to their actual traffic.
 */

// ============================================
// Baseline & Benchmarks
// ============================================

/** Flat baseline for dollar projections — explicitly stated in all copy */
const BASE_VISITORS = 1000;

/** Restaurant industry conversion/revenue constants (not traffic-dependent) */
export const RESTAURANT_BENCHMARKS = {
  // Conversion rates (industry averages)
  websiteConversionRate: 0.04,        // 4% — Google/Hospitality studies
  menuViewToConversion: 0.08,         // 8% of menu viewers convert
  retargetingConversionRate: 0.026,   // 2.6% — retargeted visitors
  coldAudienceConversionRate: 0.003,  // 0.3% — cold ad audiences

  // Average check / order values
  avgCheckSize: 45,

  // Ad performance
  smartBiddingROAS: { low: 4, high: 8 },     // $/$ with conversion tracking
  manualBiddingROAS: { low: 1, high: 2 },     // $/$ without
} as const;

/**
 * Improvement benchmarks — the defensible rates from published research.
 * These are the REAL numbers; they don't depend on traffic assumptions.
 */
export const IMPROVEMENT_RATES = {
  // Speed (Google / Akamai / Deloitte research)
  perSecondDelayConversionLoss: 0.07,   // 7% fewer conversions per second of delay
  mobileAbandonAt3s: 0.53,             // 53% leave if load > 3s (Google 2018)
  performanceLift90Plus: 0.15,          // 15% more engagement above score 90
  clsMisTapRate: 0.70,                  // 70% of layout shifts cause mis-taps

  // SEO (Backlinko / Search Engine Journal / Moz)
  titleOptCTRLift: 0.25,               // 20-30% CTR improvement
  metaDescCTRLift: 0.075,              // 5-10% CTR improvement
  schemaCTRLift: 0.25,                 // 25%+ CTR with rich results
  hoursCallReduction: 0.30,            // 30% fewer "are you open?" calls
  sitemapIndexingGain: 0.20,           // ~20% of pages may be unindexed

  // Tracking (Google / Meta / industry)
  retargetingVsColdMultiplier: 10,     // 10x conversion rate vs cold
  adBlockerRate: 0.30,                 // ~30% of users block tracking
  serverSideRecovery: 0.175,           // 15-20% data recovery
} as const;

const B = IMPROVEMENT_RATES;
const R = RESTAURANT_BENCHMARKS;
const V = BASE_VISITORS;

// ============================================
// Types
// ============================================

export interface SuccessMetric {
  icon: 'trending-up' | 'dollar-sign' | 'target' | 'users' | 'search' | 'bar-chart';
  headline: string;
  projection: string;
  impact: string;
  confidence: 'high' | 'medium' | 'low';
}

export interface SuccessProjection {
  title: string;
  summary: string;
  metrics: SuccessMetric[];
}

// ============================================
// Helpers
// ============================================

/** Dollar amount explicitly tied to the 1K baseline */
const perKDollar = (amount: number) =>
  `$${Math.round(amount).toLocaleString()}`;

const pctLabel = (rate: number) =>
  `${Math.round(rate * 100)}%`;

// ============================================
// Main Generator
// ============================================

export function generateWhatSuccessLooksLike(
  segment: 'overview' | 'vitals' | 'seo' | 'tracking',
  site: any
): SuccessProjection {
  switch (segment) {
    case 'vitals':
      return generateVitalsSuccess(site);
    case 'seo':
      return generateSeoSuccess(site);
    case 'tracking':
      return generateTrackingSuccess(site);
    case 'overview':
    default:
      return generateOverviewSuccess(site);
  }
}

// ============================================
// Vitals Success
// ============================================

function generateVitalsSuccess(site: any): SuccessProjection {
  const metrics: SuccessMetric[] = [];
  const perf = site.vitals?.performanceScore ?? 0;
  const lcp = site.vitals?.metrics?.lcp?.value ?? 0;
  const cls = site.vitals?.metrics?.cls?.value ?? 0;
  const fcp = site.vitals?.metrics?.fcp?.value ?? 0;
  const ttfb = site.vitals?.metrics?.ttfb?.value ?? 0;

  // LCP improvement
  if (lcp > 2.5) {
    const excessSeconds = lcp - 2.5;
    const lossRate = excessSeconds * B.perSecondDelayConversionLoss;
    const recoveredPerK = V * R.websiteConversionRate * lossRate;
    const revenuePerK = recoveredPerK * R.avgCheckSize;

    metrics.push({
      icon: 'trending-up',
      headline: `${pctLabel(lossRate)} Conversion Loss from Slow Loading`,
      projection: `Every second beyond the 2.5s target costs ~7% in conversions (Google research). Your LCP of ${lcp.toFixed(1)}s means an estimated ${pctLabel(lossRate)} of potential conversions are lost before the page finishes loading. That's ~${perKDollar(revenuePerK)}/mo in recovered revenue per 1,000 monthly visitors.`,
      impact: `${pctLabel(lossRate)} recovery`,
      confidence: 'high',
    });
  } else if (lcp > 0) {
    metrics.push({
      icon: 'trending-up',
      headline: 'Strong Load Speed Protects Conversions',
      projection: `Your LCP of ${lcp.toFixed(1)}s is within the 2.5s target. Research shows sites at this speed see 24% higher conversion rates than slower competitors. You're already avoiding the 7%-per-second penalty that hits most restaurant sites.`,
      impact: 'Already achieving',
      confidence: 'high',
    });
  }

  // CLS
  if (cls > 0.1) {
    metrics.push({
      icon: 'target',
      headline: '70% of Layout Shifts Cause Mis-Taps',
      projection: `Your CLS of ${cls.toFixed(2)} is above the 0.1 threshold. Research shows ~70% of layout shift events on mobile cause accidental clicks — users tap a button, the page shifts, and they land on the wrong element. This drives frustration and bounce rates, especially on reservation and menu pages.`,
      impact: 'Reduced bounce rate',
      confidence: 'high',
    });
  }

  // Performance → engagement
  if (perf < 90) {
    const liftRate = B.performanceLift90Plus;
    const additionalConversionsPerK = V * liftRate * R.menuViewToConversion;
    const revenuePerK = additionalConversionsPerK * R.avgCheckSize;

    metrics.push({
      icon: 'users',
      headline: `${pctLabel(liftRate)} More Menu Engagement Above Score 90`,
      projection: `Sites scoring 90+ on performance see ~15% more menu page views than those below (Deloitte/Google). Your score of ${perf} means visitors are less likely to browse your full menu before deciding. Reaching 90+ could translate to ~${perKDollar(revenuePerK)}/mo per 1,000 visitors from increased menu-to-conversion rates.`,
      impact: `+${pctLabel(liftRate)} engagement`,
      confidence: 'medium',
    });
  }

  // Mobile FCP abandonment
  if (fcp > 1.8) {
    const abandonRate = fcp > 3 ? B.mobileAbandonAt3s : B.mobileAbandonAt3s * 0.5;

    metrics.push({
      icon: 'dollar-sign',
      headline: `${pctLabel(abandonRate)} of Mobile Visitors May Leave`,
      projection: `With FCP at ${fcp.toFixed(1)}s, Google's research indicates ${pctLabel(abandonRate)} of mobile users leave before seeing any content. Since ~70% of restaurant searches happen on mobile, improving FCP to under 1.8s directly reduces this abandonment rate.`,
      impact: `${pctLabel(abandonRate)} mobile abandonment`,
      confidence: 'high',
    });
  }

  // TTFB
  if (ttfb > 0.8) {
    metrics.push({
      icon: 'trending-up',
      headline: 'Slow Server Response Bottlenecks Everything',
      projection: `Your server takes ${(ttfb * 1000).toFixed(0)}ms to respond (target: under 800ms). TTFB is the foundation — no other metric can be faster than the server response. A CDN typically reduces TTFB by 50-70%, which cascades into improved LCP, FCP, and overall performance score.`,
      impact: 'Foundation fix',
      confidence: 'high',
    });
  }

  return {
    title: perf >= 80 ? 'Maintaining Your Speed Advantage' : 'What Fixing Site Speed Could Mean',
    summary: perf >= 80
      ? 'Your site speed is a competitive advantage. Here\'s what that means for conversions.'
      : 'Speed has a direct, measurable impact on conversions. All percentages below are from published Google and Deloitte research.',
    metrics: metrics.slice(0, 5),
  };
}

// ============================================
// SEO Success
// ============================================

function generateSeoSuccess(site: any): SuccessProjection {
  const metrics: SuccessMetric[] = [];
  const score = site.seo?.score ?? 0;
  const pages = site.seo?.pages || [];
  const totalPages = pages.length || 1;
  const missingTitles = pages.filter((p: any) => !p.title).length;
  const missingDesc = pages.filter((p: any) => !p.metaDescription).length;
  const localSeo = site.seo?.localSeo;
  const hasSchema = localSeo?.localBusinessSchema?.found;
  const hasHours = localSeo?.hasOpeningHours;
  const hasSitemap = site.seo?.technical?.hasSitemap;

  // Title optimization
  if (missingTitles > 0 || (site.seo?.qualityAnalysis?.title?.score ?? 100) < 70) {
    const pagesNeedingWork = missingTitles > 0 ? missingTitles : Math.ceil(totalPages * 0.4);
    const pageRatio = pagesNeedingWork / totalPages;
    const effectiveLift = B.titleOptCTRLift * pageRatio;
    // Per 1K visitors: assume 40% come from organic, lift applies to those
    const additionalVisitorsPerK = V * 0.4 * effectiveLift;
    const revenuePerK = additionalVisitorsPerK * R.websiteConversionRate * R.avgCheckSize;

    metrics.push({
      icon: 'search',
      headline: `${pctLabel(B.titleOptCTRLift)} CTR Increase from Title Optimization`,
      projection: `Optimizing title tags on ${pagesNeedingWork} of ${totalPages} pages could lift organic click-through rates by 20-30% (Backlinko/Moz research). That's ~${Math.round(additionalVisitorsPerK)} additional organic visitors and ~${perKDollar(revenuePerK)}/mo in revenue per 1,000 monthly visitors.`,
      impact: `+${pctLabel(B.titleOptCTRLift)} organic CTR`,
      confidence: 'high',
    });
  }

  // Meta descriptions
  if (missingDesc > 0) {
    const pageRatio = missingDesc / totalPages;
    const effectiveLift = B.metaDescCTRLift * pageRatio;
    const additionalVisitorsPerK = V * 0.4 * effectiveLift;
    const revenuePerK = additionalVisitorsPerK * R.websiteConversionRate * R.avgCheckSize;

    metrics.push({
      icon: 'target',
      headline: `${missingDesc} Page${missingDesc === 1 ? '' : 's'} Missing Meta Descriptions`,
      projection: `Meta descriptions are the preview text that convinces searchers to click. ${missingDesc} of ${totalPages} pages are missing them. Adding compelling descriptions with your cuisine, location, and a CTA typically improves CTR by 5-10%. That's ~${perKDollar(revenuePerK)}/mo per 1,000 visitors.`,
      impact: `+${pctLabel(B.metaDescCTRLift)} CTR on ${missingDesc} pages`,
      confidence: 'high',
    });
  }

  // LocalBusiness schema
  if (!hasSchema) {
    const additionalVisitorsPerK = V * 0.4 * B.schemaCTRLift;
    const revenuePerK = additionalVisitorsPerK * R.websiteConversionRate * R.avgCheckSize;

    metrics.push({
      icon: 'dollar-sign',
      headline: '25%+ CTR Lift from Rich Results in Google',
      projection: `Adding LocalBusiness schema enables rich search results showing hours, rating, price range, and cuisine directly in Google. Restaurants with rich results see 25%+ higher click-through rates (Search Engine Journal). That's ~${perKDollar(revenuePerK)}/mo per 1,000 visitors from organic search alone.`,
      impact: `+${pctLabel(B.schemaCTRLift)} search CTR`,
      confidence: 'medium',
    });
  } else {
    metrics.push({
      icon: 'dollar-sign',
      headline: 'Schema Markup Driving Rich Results',
      projection: `Your LocalBusiness schema is active, enabling rich snippets in Google with your hours, cuisine type, and business details. This gives you a 25%+ CTR advantage over competitors without schema. Keep it updated as hours and menus change.`,
      impact: 'Already achieving',
      confidence: 'high',
    });
  }

  // Opening hours
  if (!hasHours && localSeo) {
    metrics.push({
      icon: 'users',
      headline: '30% Fewer "Are You Open?" Calls',
      projection: `Displaying structured opening hours in search results reduces unnecessary phone inquiries by ~30% (Google Business Profile data). This frees up host staff for in-house guests instead of answering the same question repeatedly.`,
      impact: '-30% phone inquiries',
      confidence: 'medium',
    });
  }

  // Sitemap
  if (!hasSitemap) {
    const unindexedPages = Math.ceil(totalPages * B.sitemapIndexingGain);

    metrics.push({
      icon: 'search',
      headline: `~${pctLabel(B.sitemapIndexingGain)} of Pages May Not Be Indexed`,
      projection: `Without an XML sitemap, an estimated ${unindexedPages} of ${totalPages} pages may not appear in Google search results. This means seasonal menus, event pages, and specials could be invisible — exactly the high-intent content that drives reservations.`,
      impact: `${unindexedPages} pages at risk`,
      confidence: 'high',
    });
  }

  return {
    title: score >= 85 ? 'Building on Your SEO Strength' : 'What Better SEO Could Mean',
    summary: score >= 85
      ? 'Your SEO foundation is strong. Here\'s how to widen the gap against competitors.'
      : 'Every SEO improvement means more potential customers finding you instead of the restaurant down the street. Percentages are from published research; dollar estimates assume 1,000 monthly visitors.',
    metrics: metrics.slice(0, 5),
  };
}

// ============================================
// Tracking Success
// ============================================

function generateTrackingSuccess(site: any): SuccessProjection {
  const metrics: SuccessMetric[] = [];
  const hasGTM = site.tracking?.gtm?.present;
  const hasGA4 = site.tracking?.ga4?.present;
  const hasFBPixel = site.tracking?.facebookPixel?.present;
  const hasGoogleAds = site.tracking?.googleAds?.present;
  const coverage = site.tracking?.events?.trackingCoverage;
  const coverageScore = coverage?.overallScore ?? 0;
  const gaps = site.tracking?.events?.trackingGaps || [];
  const criticalGaps = gaps.filter((g: any) => g.priority === 'critical').length;
  const gtmData = site.tracking?.events?.gtmContainerData;
  const hasCallTracking = gtmData?.hasCallTracking;
  const trackedItems = site.tracking?.events?.trackedItems || [];
  const adsConversions = gtmData?.googleAdsConversions?.length ?? 0;

  // 1. Meta retargeting
  if (!hasFBPixel) {
    metrics.push({
      icon: 'dollar-sign',
      headline: 'No Meta Pixel = No Retargeting',
      projection: `Without Facebook/Meta Pixel, you can't retarget website visitors on Instagram and Facebook. Retargeting converts at ${B.retargetingVsColdMultiplier}x the rate of cold audiences (${pctLabel(R.retargetingConversionRate)} vs ${pctLabel(R.coldAudienceConversionRate)}). For every 1,000 monthly visitors, that's ~${Math.round(V * R.retargetingConversionRate)} potential retargeting conversions you're missing entirely.`,
      impact: `${B.retargetingVsColdMultiplier}x conversion opportunity`,
      confidence: 'high',
    });
  } else if (hasFBPixel && hasGA4) {
    const hasEnoughData = trackedItems.length >= 10;

    metrics.push({
      icon: 'dollar-sign',
      headline: hasEnoughData
        ? 'Your Data Powers Lookalike Audiences'
        : 'Build Toward Lookalike Audiences',
      projection: hasEnoughData
        ? `With ${trackedItems.length} tracked events feeding your Meta Pixel, you have enough data to build Lookalike Audiences — finding new customers who behave like your best ones. Restaurants running lookalike campaigns typically see 3-5x ROAS. If you're not running retargeting and lookalike campaigns yet, this data is going unused.`
        : `Your Meta Pixel is collecting data but needs more tracked events to build effective Lookalike Audiences. Once tracking coverage reaches 70%+, you'll have the data foundation for 3-5x ROAS campaigns.`,
      impact: hasEnoughData ? '3-5x ROAS potential' : 'Building data foundation',
      confidence: hasEnoughData ? 'medium' : 'low',
    });
  }

  // 2. Tracking gaps
  if (coverageScore < 70 && criticalGaps > 0) {
    const blindSpotRate = 1 - (coverageScore / 100);

    metrics.push({
      icon: 'bar-chart',
      headline: `${pctLabel(blindSpotRate)} of Conversions Are Invisible`,
      projection: `With ${coverageScore}% tracking coverage, roughly ${pctLabel(blindSpotRate)} of conversions go unmeasured. You can't tell which marketing channels drive reservations vs. which waste budget. ${criticalGaps} critical gap${criticalGaps === 1 ? '' : 's'} — closing these lets you reallocate spend to what actually works.`,
      impact: `${criticalGaps} critical blind spot${criticalGaps === 1 ? '' : 's'}`,
      confidence: 'high',
    });
  }

  // 3. Google Ads tracking
  if (hasGoogleAds && adsConversions > 0) {
    metrics.push({
      icon: 'target',
      headline: `${adsConversions} Conversion Action${adsConversions === 1 ? '' : 's'} Feeding Smart Bidding`,
      projection: `Your ${adsConversions} Google Ads conversion action${adsConversions === 1 ? '' : 's'} feed${adsConversions === 1 ? 's' : ''} data back to Google's algorithm. This enables Smart Bidding, which typically delivers $${R.smartBiddingROAS.low}-$${R.smartBiddingROAS.high} return per $1 spent — vs. $${R.manualBiddingROAS.low}-$${R.manualBiddingROAS.high} without conversion tracking.`,
      impact: `${R.smartBiddingROAS.low}-${R.smartBiddingROAS.high}x ROAS`,
      confidence: 'high',
    });
  } else if (!hasGoogleAds) {
    metrics.push({
      icon: 'target',
      headline: 'No Google Ads Conversion Tracking',
      projection: `Without conversion tracking, Google can't optimize your ad delivery. Adding it enables Smart Bidding, which typically improves ROAS from $${R.manualBiddingROAS.low}-$${R.manualBiddingROAS.high}/dollar to $${R.smartBiddingROAS.low}-$${R.smartBiddingROAS.high}/dollar. On a $1,000/mo ad budget, that's the difference between $1,000-$2,000 and $4,000-$8,000 in attributed revenue.`,
      impact: `${R.smartBiddingROAS.low}-${R.smartBiddingROAS.high}x vs ${R.manualBiddingROAS.low}-${R.manualBiddingROAS.high}x ROAS`,
      confidence: 'medium',
    });
  }

  // 4. Server-side tracking
  if (hasGTM && hasGA4) {
    metrics.push({
      icon: 'search',
      headline: `~${pctLabel(B.adBlockerRate)} of Visitors Are Invisible to Tracking`,
      projection: `With ~30% of users running ad blockers, your analytics miss roughly 300 out of every 1,000 visitors. Server-side tracking bypasses client-side blockers and recovers 15-20% of that lost data — giving you a more accurate picture of which channels actually drive revenue.`,
      impact: `${pctLabel(B.serverSideRecovery)} data recovery`,
      confidence: 'high',
    });
  }

  // 5. Call tracking
  if (!hasCallTracking) {
    metrics.push({
      icon: 'users',
      headline: 'Phone Conversions Are Untracked',
      projection: `Phone reservations are invisible without call tracking. Industry data shows 30-40% of restaurant conversions happen by phone. Without tracking, you can't attribute these to the marketing that drove them — often revealing Google Ads drives more value than click-only data suggests.`,
      impact: '30-40% of conversions hidden',
      confidence: 'medium',
    });
  } else {
    metrics.push({
      icon: 'users',
      headline: 'Call Tracking Capturing Phone Revenue',
      projection: `Your call tracking is active, connecting phone reservations back to your marketing spend. This is critical — many restaurants find 30-40% of conversions happen by phone, and this data dramatically improves ROAS measurement accuracy.`,
      impact: 'Already tracking',
      confidence: 'high',
    });
  }

  // 6. Comprehensive success
  if (hasGTM && hasGA4 && hasFBPixel && coverageScore >= 70) {
    metrics.push({
      icon: 'trending-up',
      headline: 'Your Tracking Stack Is a Competitive Advantage',
      projection: `With GTM, GA4, Meta Pixel, and ${coverageScore}% event coverage, you have the data foundation most restaurants lack. Restaurants with complete tracking typically achieve 2-3x better ROAS than those without — you can optimize spend, build audiences, and attribute revenue across channels.`,
      impact: '2-3x ROAS advantage',
      confidence: 'high',
    });
  }

  return {
    title: coverageScore >= 70 && hasFBPixel && hasGA4
      ? 'Maximizing Your Tracking Investment'
      : 'What Complete Tracking Could Mean',
    summary: coverageScore >= 70 && hasFBPixel && hasGA4
      ? 'Your tracking foundation is solid. Here\'s how to extract maximum value from the data you\'re collecting.'
      : 'Every tracking gap is a missed opportunity to optimize marketing spend. ROAS benchmarks are from Google\'s published data.',
    metrics: metrics.slice(0, 5),
  };
}

// ============================================
// Overview Success (Aggregated)
// ============================================

function generateOverviewSuccess(site: any): SuccessProjection {
  const metrics: SuccessMetric[] = [];

  const perfScore = site.vitals?.performanceScore ?? 0;
  const seoScore = site.seo?.score ?? 0;
  const lcp = site.vitals?.metrics?.lcp?.value ?? 0;
  const hasFBPixel = site.tracking?.facebookPixel?.present;
  const hasGA4 = site.tracking?.ga4?.present;
  const coverageScore = site.tracking?.events?.trackingCoverage?.overallScore ?? 0;
  const hasSchema = site.seo?.localSeo?.localBusinessSchema?.found;
  const missingTitles = (site.seo?.pages || []).filter((p: any) => !p.title).length;
  const totalPages = (site.seo?.pages || []).length || 1;

  // 1. Speed impact
  if (perfScore < 80 && lcp > 2.5) {
    const excessSeconds = lcp - 2.5;
    const lossRate = excessSeconds * B.perSecondDelayConversionLoss;
    const recoveredPerK = V * R.websiteConversionRate * lossRate;
    const revenuePerK = recoveredPerK * R.avgCheckSize;

    metrics.push({
      icon: 'trending-up',
      headline: `${pctLabel(lossRate)} Conversion Loss from ${lcp.toFixed(1)}s Load Time`,
      projection: `Your LCP of ${lcp.toFixed(1)}s costs an estimated ${pctLabel(lossRate)} in conversions (7% per second beyond 2.5s). That's ~${perKDollar(revenuePerK)}/mo per 1,000 visitors in recoverable revenue.`,
      impact: `${pctLabel(lossRate)} recovery opportunity`,
      confidence: 'high',
    });
  }

  // 2. SEO visibility
  if (seoScore < 80 || missingTitles > 0) {
    const pageRatio = missingTitles > 0 ? missingTitles / totalPages : 0.3;
    const effectiveLift = B.titleOptCTRLift * pageRatio;
    const additionalPerK = V * 0.4 * effectiveLift;
    const revenuePerK = additionalPerK * R.websiteConversionRate * R.avgCheckSize;

    metrics.push({
      icon: 'search',
      headline: `${pctLabel(effectiveLift)} More Organic Visitors from SEO Fixes`,
      projection: `Title and meta description improvements could lift your organic CTR by ${pctLabel(effectiveLift)} — bringing ~${Math.round(additionalPerK)} more organic visitors and ~${perKDollar(revenuePerK)}/mo per 1,000 total monthly visitors.`,
      impact: `+${pctLabel(effectiveLift)} organic traffic`,
      confidence: 'medium',
    });
  }

  // 3. Tracking / retargeting
  if (!hasFBPixel || coverageScore < 70) {
    metrics.push({
      icon: 'dollar-sign',
      headline: `${!hasFBPixel ? 'No Retargeting + ' : ''}${coverageScore < 70 ? `${coverageScore}% Tracking Coverage` : 'Tracking Gaps'}`,
      projection: `${!hasFBPixel ? 'Without Meta Pixel, you can\'t retarget visitors (10x higher conversion rate than cold audiences). ' : ''}${coverageScore < 70 ? `At ${coverageScore}% coverage, ${pctLabel(1 - coverageScore / 100)} of conversions are unmeasured — you can't optimize what you can't see.` : ''} Closing these gaps enables data-driven ad optimization.`,
      impact: 'Marketing blind spots',
      confidence: 'high',
    });
  }

  // 4. Combined per-1K opportunity
  const speedPerK = (perfScore < 80 && lcp > 2.5)
    ? V * R.websiteConversionRate * B.perSecondDelayConversionLoss * (lcp - 2.5) * R.avgCheckSize
    : 0;
  const seoPerK = seoScore < 80
    ? V * 0.4 * B.titleOptCTRLift * 0.3 * R.websiteConversionRate * R.avgCheckSize
    : 0;
  const schemaPerK = !hasSchema
    ? V * 0.4 * B.schemaCTRLift * R.websiteConversionRate * R.avgCheckSize
    : 0;
  const totalPerK = speedPerK + seoPerK + schemaPerK;

  if (totalPerK > 50) {
    metrics.push({
      icon: 'target',
      headline: `~${perKDollar(totalPerK)}/mo Opportunity per 1,000 Visitors`,
      projection: `Addressing all speed, SEO, and schema issues represents ~${perKDollar(totalPerK)}/mo in potential revenue per 1,000 monthly visitors. These improvements compound — faster sites rank higher, better SEO brings more traffic, and both multiply conversions.`,
      impact: `${perKDollar(totalPerK)} per 1K visitors`,
      confidence: 'low',
    });
  }

  // 5. Strengths
  const strengths: string[] = [];
  if (perfScore >= 80) strengths.push('fast load times');
  if (seoScore >= 80) strengths.push('strong SEO');
  if (hasGA4 && hasFBPixel) strengths.push('complete tracking stack');
  if (hasSchema) strengths.push('rich search results');

  if (strengths.length > 0) {
    metrics.push({
      icon: 'trending-up',
      headline: 'Your Competitive Advantages',
      projection: `You're already ahead of most restaurants with ${strengths.join(', ')}. Maintaining these while addressing remaining gaps compounds over time — each improvement reinforces the others.`,
      impact: 'Competitive advantage',
      confidence: 'high',
    });
  }

  return {
    title: 'What Success Looks Like',
    summary: 'Improvement rates are from published research (Google, Backlinko, Deloitte). Dollar projections are based on a flat 1,000 monthly visitors — multiply by your actual traffic for a realistic estimate.',
    metrics: metrics.slice(0, 5),
  };
}
